<template>
	<BaseColumn
		:size="2"
		:title="title"
		:closable="false"
		:class="{hidden: closed}"
		class="sliding"
		@close="close"
	>
		<template #body>
			<div>
				<h3 class="section-title section-header">
					{{ transUcfirst('commons.details') }}
				</h3>
				<div class="tag-group-options columns">
					<div>
						<FormField
							:label="transUcfirst('commons.name')"
							:error="formErrors.name"
						>
							<FormInput
								v-model="name"
								:disabled="readOnly"
							>
								<template #trailing>
									<Icon
										icon-name="pen"
										size="md"
									/>
								</template>
							</FormInput>
						</FormField>
					</div>
					<div>
						<div class="visibility">
							<FormField
								:label="transUcfirst('commons.visibility')"
								:error="formErrors.visibility"
							>
								<VisibilityDropdown
									:value="visibility"
									:is-small="true"
									:disabled="readOnly"
									@input="onVisibilityChange"
								/>
							</FormField>
						</div>
					</div>
				</div>
			</div>
			<div>
				<div class="tag-group-sections">
					<div class="columns">
						<div>
							<SectionButtons
								v-if="isSectionAvailable(SECTION_DEFAULT)"
								:buttons="sectionButtons[SECTION_DEFAULT]"
								:title="translateSectionLabel('default', isPlayerCompetition)"
								:section-name="SECTION_DEFAULT"
								:enabled="sectionStatuses[SECTION_DEFAULT]"
								:editable="!tagGroup.isSystemDefault"
								:buttons-editable="false"
								@section-toggle="onSectionToggle"
							/>
							<SectionButtons
								v-if="isSectionAvailable(SECTION_ADDITIONAL)"
								:buttons="sectionButtons[SECTION_ADDITIONAL]"
								:title="translateSectionLabel('additional', isPlayerCompetition)"
								:section-name="SECTION_ADDITIONAL"
								:enabled="sectionStatuses[SECTION_ADDITIONAL]"
								:editable="!tagGroup.isSystemDefault"
								:buttons-editable="false"
								@section-toggle="onSectionToggle"
							/>
						</div>
						<div>
							<SectionSwitch
								v-for="section of emptySections"
								:key="`section-switch-${section.name}`"
								:value="sectionStatuses[section.name]"
								:editable="!tagGroup.isSystemDefault"
								:section-name="section.name"
								:label="section.label"
								class="empty-section-switch"
								@input="onSectionToggle(section.name, $event)"
							/>
							<SectionButtons
								v-if="isSectionAvailable(SECTION_CUSTOM_EVENT)"
								:buttons="sectionButtons[SECTION_CUSTOM_EVENT]"
								:title="translateSectionLabel('custom_event', isPlayerCompetition)"
								:section-name="SECTION_CUSTOM_EVENT"
								:enabled="sectionStatuses[SECTION_CUSTOM_EVENT]"
								:editable="hasCustomEventButtons && !tagGroup.isSystemDefault"
								@section-toggle="onCustomSectionToggle"
								@btn-edit="onCustomButtonEdit"
								@btn-delete="onCustomButtonDelete($event, SECTION_CUSTOM_EVENT)"
							>
								<template #extra>
									<button
										class="button--secondary button button--icon"
										@click="showCustomButtonCreateForm(SECTION_CUSTOM_EVENT)"
									>
										<Icon
											icon-name="e-add"
											size="lg"
										/>
										<span>
											{{ labels.addButton }}
										</span>
									</button>
								</template>
							</SectionButtons>
							<SectionButtons
								v-if="isSectionAvailable(SECTION_CUSTOM_PARTICIPANT)"
								:buttons="sectionButtons[SECTION_CUSTOM_PARTICIPANT]"
								:title="translateSectionLabel('custom_participant', isPlayerCompetition)"
								:section-name="SECTION_CUSTOM_PARTICIPANT"
								:enabled="sectionStatuses[SECTION_CUSTOM_PARTICIPANT]"
								:editable="hasCustomParticipantButtons && !tagGroup.isSystemDefault"
								@section-toggle="onCustomSectionToggle"
								@btn-edit="onCustomButtonEdit"
								@btn-delete="onCustomButtonDelete($event, SECTION_CUSTOM_PARTICIPANT)"
							>
								<template #extra>
									<button
										class="button--secondary button button--icon"
										@click="showCustomButtonCreateForm(SECTION_CUSTOM_PARTICIPANT)"
									>
										<Icon
											icon-name="e-add"
											size="lg"
										/>
										<span>
											{{ labels.addButton }}
										</span>
									</button>
								</template>
							</SectionButtons>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<div class="button-group">
				<button
					class="button button--secondary"
					@click="cancelForm"
				>
					{{ transUcfirst('commons.cancel') }}
				</button>
				<button
					class="button button--responsive button--primary"
					:disabled="!canSave"
					@click="saveForm"
				>
					{{ transUcfirst('commons.save') }}
				</button>
			</div>
			<!-- Add/edit custom button forms -->
			<CustomButtonForm
				v-if="isCreatingCustomButton"
				:title="transUcfirst('tag_group.tag_button_create')"
				@close="isCreatingCustomButton = false"
				@save="createCustomButton"
			/>
			<CustomButtonForm
				v-if="editingCustomButton"
				:title="transUcfirst('tag_group.tag_button_edit')"
				:tag-button="editingCustomButton"
				@close="editingCustomButton = null"
				@save="updateCustomButton"
			/>
		</template>
	</BaseColumn>
</template>

<script>
import Vue from 'vue';
import TagGroup, {
	SECTION_ADDITIONAL,
	SECTION_CUSTOM_EVENT,
	SECTION_CUSTOM_PARTICIPANT,
	SECTION_DEFAULT,
	SECTION_COMPETITION,
	SECTION_SCOREBOARD,
	SECTION_MARKING_EVENT,
	SECTION_RUN,
	SECTION_MARKING_ATHLETE,
} from '@videotools/js/api/entities/TagGroup';
import BaseColumn from '@videotools/js/one/components/Settings/BaseColumn';
import FormField from '@videotools/js/one/components/Form/FormField';
import FormInput from '@videotools/js/one/components/Form/FormInput';
import SectionButtons from '@videotools/js/one/components/TagGroups/SectionButtons';
import { mapGetters } from 'vuex';
import { validators } from '@videotools/js/form/form-validator';
import SectionSwitch from '@videotools/js/one/components/TagGroups/SectionSwitch';
import VisibilityIconMixin from '@videotools/js/mixins/visibility-icon';
import VisibilityDropdown from '@videotools/js/one/components/TagGroups/VisibilityDropdown';
import FormValidationMixin from '@videotools/js/mixins/form-validation';
import TagButton, { TAG_TARGET_EVENT, TAG_TARGET_PLAYER, TAG_TARGET_TEAM, TAG_TYPE_CUSTOM } from '@videotools/js/api/entities/TagButton';
import CustomButtonForm from '@videotools/js/one/components/TagGroups/CustomButtonForm';
import SectionLabelsMixin from '@videotools/js/one/components/TagGroups/mixin/SectionLabelsMixin';

export default {
	name:       'TagGroupForm',
	components: { CustomButtonForm, VisibilityDropdown, SectionSwitch, SectionButtons, FormInput, FormField, BaseColumn },
	mixins:     [VisibilityIconMixin, FormValidationMixin, SectionLabelsMixin],
	props:      {
		tagGroup: {
			type:     TagGroup,
			required: true,
		},
		isSaving: {
			type:    Boolean,
			default: false,
		},
		title: {
			type: String,
		},
	},
	data() {
		return {
			name:                   null,
			visibility:             null,
			sectionStatuses:        {},
			sectionButtons:         {},
			closed:                 false,
			isCreatingCustomButton: false,
			newCustomButtonSection: null,
			editingCustomButton:    null,
			modifiedCustomButtons:  [],
			SECTION_DEFAULT,
			SECTION_ADDITIONAL,
			SECTION_MARKER:         SECTION_MARKING_EVENT,
			SECTION_COMPETITION,
			SECTION_SCOREBOARD,
			SECTION_CUSTOM_EVENT,
			SECTION_CUSTOM_PARTICIPANT,
		};
	},
	computed: {
		...mapGetters({
			defaultTagButtons:        'tagGroups/defaultSectionButtons',
			additionalSectionButtons: 'tagGroups/additionalSectionButtons',
			isPlayerCompetition:      'event/isPlayerCompetition',
			tagGroupSectionStatuses:  'tagGroups/tagGroupSectionStatuses',
			availableSections:        'tagGroups/availableSections',
		}),
		labels() {
			return {
				addButton: this.transUcfirst('commons.type_add', { type: this.transChoiceUcFirst('commons.button', 1) }),
			};
		},
		readOnly() {
			return this.tagGroup.isPublic;
		},
		canSave() {
			return !this.hasFormErrors && !this.isSaving;
		},
		emptySections() {
			return this.availableSections
				.filter(s => ([
					SECTION_MARKING_EVENT,
					SECTION_COMPETITION,
					SECTION_SCOREBOARD,
					SECTION_RUN,
					SECTION_MARKING_ATHLETE,
				].includes(s)))
				.map(sectionName => ({
					name:  sectionName,
					label: this.translateSectionLabel(sectionName, this.isPlayerCompetition),
				}));
		},
		enabledSections() {
			return Object.keys(this.sectionStatuses)
				.filter(section => this.sectionStatuses[section]);
		},
		allCustomButtons() {
			return [
				...this.sectionButtons[SECTION_CUSTOM_EVENT],
				...this.sectionButtons[SECTION_CUSTOM_PARTICIPANT],
			];
		},
		hasCustomEventButtons() {
			return this.sectionButtons[SECTION_CUSTOM_EVENT].length > 0;
		},
		hasCustomParticipantButtons() {
			return this.sectionButtons[SECTION_CUSTOM_PARTICIPANT].length > 0;
		},
		customButtonsChanges() {
			const initialButtons = this.tagGroup.customButtons;
			const currentButtons = this.allCustomButtons;

			return {
				added:   currentButtons.filter(btn => btn.isNew),
				updated: this.modifiedCustomButtons,
				deleted: initialButtons.filter(btn => !btn.isNew && !currentButtons.includes(btn)),
			};
		},
	},
	created() {
		this.initForm();
		this.initValidator({
			name: {
				fn:    validators.required,
				error: this.transUcfirst('validation.required', { attribute: this.transUcfirst('validation.attributes.name') }),
			},
			visibility: {
				fn:    validators.required,
				error: this.transUcfirst('validation.required', { attribute: this.transUcfirst('commons.visibility') }),
			},
		}, {
			name:       this.name,
			visibility: this.visibility,
		});
		this.$watch('name', this.onNameChange);
	},
	methods: {
		initForm() {
			this.sectionStatuses = Object.assign({}, this.tagGroupSectionStatuses(this.tagGroup));
			this.sectionButtons = {
				[SECTION_DEFAULT]:            [...this.defaultTagButtons],
				[SECTION_ADDITIONAL]:         [...this.additionalSectionButtons],
				[SECTION_CUSTOM_EVENT]:       [...this.tagGroup.customEventButtons],
				[SECTION_CUSTOM_PARTICIPANT]: [...this.tagGroup.customParticipantButtons],
			};
			this.name = this.tagGroup.name;
			this.visibility = this.tagGroup.visibility;
		},
		onNameChange(value) {
			this.validateField('name', value);
		},
		onVisibilityChange(selected) {
			this.visibility = selected;
			this.validateField('visibility', selected);
		},
		saveForm() {
			this.$emit('save', {
				name:          this.name,
				visibility:    this.visibility,
				sections:      this.enabledSections,
				customButtons: this.customButtonsChanges,
			});
		},
		cancelForm() {
			this.close();
		},
		onSectionToggle(sectionName, status) {
			Vue.set(this.sectionStatuses, sectionName, status);
			if (sectionName === SECTION_SCOREBOARD && status) {
				// also enable match section when scoreboard is enabled
				Vue.set(this.sectionStatuses, SECTION_COMPETITION, true);
			}
			if (sectionName === SECTION_COMPETITION && !status) {
				// also disable scoreboard is match is disabled
				Vue.set(this.sectionStatuses, SECTION_SCOREBOARD, false);
			}
		},
		onCustomSectionToggle(sectionName, status) {
			if (status && !this.sectionButtons[sectionName].length) {
				return;
			}
			Vue.set(this.sectionStatuses, sectionName, status);
		},
		showCustomButtonCreateForm(section) {
			this.isCreatingCustomButton = true;
			this.newCustomButtonSection = section;
		},
		createCustomButton({ id, name, duration }) {
			let target;
			if (this.newCustomButtonSection === SECTION_CUSTOM_EVENT) {
				target = TAG_TARGET_EVENT;
			} else if (this.newCustomButtonSection === SECTION_CUSTOM_PARTICIPANT) {
				if (this.isPlayerCompetition) {
					target = TAG_TARGET_PLAYER;
				} else {
					target = TAG_TARGET_TEAM;
				}
			}

			if (!target) {
				throw new Error('Missing target when creating a new tag button');
			}
			const button = new TagButton({
				id,
				name,
				type:          TAG_TYPE_CUSTOM,
				secondsBefore: duration.preRoll,
				secondsAfter:  duration.postRoll,
				target,
			});

			this.sectionButtons[this.newCustomButtonSection].push(button);
			this.onCustomSectionToggle(this.newCustomButtonSection, true);
			this.isCreatingCustomButton = false;
			this.newCustomButtonSection = null;
		},
		updateCustomButton({ id, name, duration }) {
			const existing = this.allCustomButtons.find(btn => btn.id === id);

			if (existing && !existing.isNew) {
				if (name !== existing.name ||
					existing.secondsBefore !== duration.preRoll ||
					existing.secondsAfter !== duration.postRoll
				) {
					// add the custom button to the list of modified buttons
					this.modifiedCustomButtons =
						[
							...this.modifiedCustomButtons.filter(btn => btn.id !== existing.id),
						 existing,
						];
				}
			}
			// update
			existing.name = name;
			existing.secondsBefore = duration.preRoll;
			existing.secondsAfter = duration.postRoll;

			this.editingCustomButton = null;
		},
		onCustomButtonEdit(btnId) {
			this.editingCustomButton =
				this.allCustomButtons.find(btn => btn.id === btnId);
		},
		async onCustomButtonDelete(btnId, section) {
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('commons.button', 1),
					}),
					body: this.$I18n.trans('commons.delete_confirmation'),
				});
				this.doDelete(btnId, section);
			} catch (e) {
				// do nothing, most likely confirmation canceled
			}
		},
		doDelete(btnId, section) {
			this.sectionButtons[section] =
				this.sectionButtons[section].filter(btn => btn.id !== btnId);

			if (this.sectionStatuses[section] && this.sectionButtons[section].length === 0) {
				this.sectionStatuses[section] = false;
			}
		},
		close() {
			setTimeout(() => this.$emit('close'), 250);
			this.closed = true;
		},
		isSectionAvailable(section) {
			return this.availableSections.includes(section);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.sliding {
	animation: slide-in 300ms ease-out;
	position: relative;

	&.hidden {
		animation: slide-out 300ms;
	}

	@keyframes slide-in {
		0% {
			transform: translate(1000px, 0px);
		}

		100% {
			transform: translate(0, 0);
		}
	}

	@keyframes slide-out {
		0% {
			transform: translate(0, 0);
		}

		100% {
			transform: translate(1000px, 0);
		}
	}
}

::v-deep .body {
	overflow: auto;

	> * {
		&:last-child {
			flex: 1;
		}
	}
}

.visibility {
	width: 200px;
}

.tag-group-options {
	display: flex;

	.col1 {
		flex: 1;
		margin-right: 24px;
	}

	.col2 {
		display: flex;
	}
}

.tag-group-sections {
	margin-top: 35px;
}

.form-group {
	margin: 0;
}

::v-deep .form-field--input-wrapper {
	display: flex;
}

.columns {
	display: flex;

	> * {
		flex: 1;

		&:first-child {
			margin-right: 48px;
		}
	}
}

.add-button {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px;
	width: 204px;

	> * {
		margin-right: 16px;
	}
}

::v-deep .footer {
	border-top: 1px solid var(--color-grey-850);
}

.settings-column {
	background: var(--color-grey-900);
	display: flex;
	flex-direction: column;
}

.empty-section-switch {
	border-bottom: 1px solid var(--color-grey-850);
}

.section-buttons:not(:last-child) {
	padding-bottom: 32px;
	border-bottom: 1px solid var(--color-grey-850)
}
</style>
